/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  formatAmount,
  webFormatCurrency,
  installMentPlans,
} from '@rb-serve-app/core';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { BalanceIcon } from '../assets/svg/StatusIcon';

interface PlanCardProps {
  plan: installMentPlans;
  borderColor: string;
  plantype: string;
  handleViewEPP?: (plan: installMentPlans) => void;
}

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  borderColor,
  plantype,
  handleViewEPP,
}) => {
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const lang = localStorage.getItem('language') || 'en-US';

  const { entries } = contentfulData;
  const contentData = entries?.rb_view_epp;
  if (!plan || !contentData) return null;

  // Simplified UI for pending plans
  if (plantype === 'pending') {
    return (
      <div className="rounded-[5px] border border-[#8F5F00] bg-white p-4">
        <div className="flex justify-between items-start mb-3">
          <h3
            className="text-base text-black font-[650]"
            aria-label={plan.planName || contentData?.pendingPlan}
          >
            {plan.planName || contentData?.pendingPlan}
          </h3>
          <p
            className="text-base font-bold text-black"
            aria-label={plan?.originalTransactionAmount?.value}
          >
            ${plan?.originalTransactionAmount?.value || '0.00'}
          </p>
        </div>
        <p
          className="text-sm text-black"
          aria-label={contentData?.pendingPlanDescription}
        >
          {contentData?.pendingPlanDescription}
        </p>
      </div>
    );
  }

  return (
    <div
      key={plan.planName}
      className={`p-4 border-b border-light-gray-border ${borderColor} rounded-md shadow-sm md:border`}
    >
      <div className="flex justify-between items-start lg:items-center mb-3 flex-col lg:flex-row">
        <h3 className="text-base text-black font-[650]">{plan.planName}</h3>
        {plan?.planStatus === 'ACTIVE' &&
          plan?.outstandingInstallmentAmount?.value && (
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600 font-[600]">
                {plan?.outstandingInstallmentAmount?.value
                  ? `${webFormatCurrency(Number(plan.outstandingInstallmentAmount.value) || 0, lang)} `
                  : '--.--'}
              </p>
              <div className="relative group">
                <span className="cursor-pointer">
                  <BalanceIcon fill="#DA291C" width="14" height="14" />
                </span>
                <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-50 text-black text-xs rounded px-2 py-1 border border-gray-400 w-max font-[400] z-10">
                  {contentData?.minimumMonthlyAmountMain ||
                    'Monthly payment amount'}
                </div>
              </div>
            </div>
          )}
        {plan?.planStatus !== 'ACTIVE' &&
          plan?.outstandingInstallmentAmount?.value && (
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600 font-[600]">
                {plan?.outstandingInstallmentAmount?.value
                  ? `${webFormatCurrency(Number(plan.outstandingInstallmentAmount.value) || 0, lang)}`
                  : '--.--'}
                {/* {plan?.numberOfInstallmentsPaid} {contentData.paymentsTitle}
                {')'} */}
              </p>
              <div className="relative group">
                <span className="cursor-pointer">
                  <BalanceIcon fill="#DA291C" width="14" height="14" />
                </span>
                <div className="absolute bottom-full mb-1 hidden group-hover:block bg-gray-50 text-black text-xs rounded px-2 py-1 border border-gray-400 w-max font-[400] z-10">
                  {contentData?.minimumMonthlyAmountMain ||
                    'Monthly payment amount'}
                </div>
              </div>
            </div>
          )}
      </div>
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600 font-[500] ml-[2px]">
          {contentData.paidTitle}{' '}
          <span className="text-sm font-bold text-gray-800">
            {plan?.principleCompleted !== undefined
              ? `${webFormatCurrency(Number(plan.principleCompleted) || 0, lang)}`
              : '--.--'}
          </span>
        </p>
        <p className="text-sm text-gray-600 font-[500]">
          {contentData.remainingTitle}{' '}
          <span className="text-sm font-bold text-gray-800">
            {plan?.planStatus === 'ACTIVE'
              ? `${webFormatCurrency(Number(plan?.outstandingTransactionAmount?.value) || 0, lang)}`
              : webFormatCurrency(0.0, lang)}
          </span>
        </p>
      </div>

      <div className="mt-2">
        <div
          className="h-2 w-full bg-gray-300 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={plan?.completedPercentage}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className={`h-full ${plantype === 'cancelled' ? 'bg-progressbar-deactive' : 'bg-progressbar-success'}`}
            style={{ width: `${plan.completedPercentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1 ml-[2px] font-[500]">
          {plantype === 'cancelled'
            ? contentData.eppStatusCancelledSubtitle
            : contentData.eppStatusCompletedSubtitle.replace(
                '%s',
                `${plan.completedPercentage.toString()}%`
              )}
        </p>
      </div>
      <button
        className="mt-4 w-full text-left text-sm font-bold text-primary-red hover:underline"
        type="button"
        aria-label={`View ${plan.planName} equal payment plan`}
        onClick={() => handleViewEPP?.(plan)}
      >
        {contentData.viewEppCtaTitle}
      </button>
    </div>
  );
};

export default PlanCard;
